import { useLocation } from "react-router-dom";
import { useGetTopic, useGetLesson } from "../api/queries";
import { getPageTitleFromUrl } from "../utils/pageTitleFromUrl";

interface PageTitleContext {
  pageTitle: string;
  lessonTitle?: string;
  moduleTitle?: string;
  lessonId?: string;
  moduleId?: string;
  isLoading: boolean;
}

const OBJECT_ID_PATTERN = "[a-fA-F0-9]{24}";
const SLUG_OR_ID_PATTERN = "[a-z0-9-]+|[a-fA-F0-9]{24}";

export const usePageTitle = (): PageTitleContext => {
  const location = useLocation();
  const pathname = location.pathname;

  // Extract IDs or slugs using patterns that match both
  const moduleMatch = pathname.match(
    new RegExp(`/topics/(${SLUG_OR_ID_PATTERN})`),
  );
  const lessonMatch = pathname.match(
    new RegExp(
      `/topics/(?:${SLUG_OR_ID_PATTERN})/sections/(${SLUG_OR_ID_PATTERN})`,
    ),
  );
  const toolboxMatch = pathname.match(
    new RegExp(`/my-toolboxes/(${OBJECT_ID_PATTERN})`),
  );
  const editLessonMatch = pathname.match(
    new RegExp(`/admin/edit-lesson/(${OBJECT_ID_PATTERN})`),
  );
  const teleprompterMatch = pathname.match(
    new RegExp(`/teleprompters/(${OBJECT_ID_PATTERN})`),
  );

  const moduleId = moduleMatch?.[1];
  const lessonId = lessonMatch?.[1];
  const toolboxModuleId = toolboxMatch?.[1];
  const editLessonId = editLessonMatch?.[1];
  const teleprompterLessonId = teleprompterMatch?.[1];

  // Consolidate IDs
  const effectiveLessonId = lessonId || editLessonId || teleprompterLessonId;
  const effectiveModuleId = moduleId || toolboxModuleId;

  // Fetch data
  const { data: moduleData, isLoading: moduleLoading } = useGetTopic(
    effectiveModuleId || "",
  );
  const { data: lessonData, isLoading: lessonLoading } = useGetLesson(
    effectiveLessonId || "",
  );

  const isLoading = moduleLoading || lessonLoading;

  // Title prefix lookup
  const lessonTitlePrefixes: Record<string, string> = {
    editLesson: "Edit: ",
    teleprompter: "Teleprompter: ",
  };

  const moduleTitleSuffixes: Record<string, string> = {
    toolbox: " - Toolbox",
  };

  // Determine page type and construct title
  const getPageType = () => {
    return editLessonId
      ? "editLesson"
      : teleprompterLessonId
        ? "teleprompter"
        : lessonId
          ? "lesson"
          : toolboxModuleId
            ? "toolbox"
            : moduleId
              ? "module"
              : "static";
  };

  const pageType = getPageType();

  // Build context based on page type
  const contextBuilders: Record<string, () => PageTitleContext> = {
    editLesson: () => ({
      pageTitle: isLoading
        ? "Loading..."
        : `${lessonTitlePrefixes.editLesson}${lessonData?.title || "Unknown"}`,
      lessonTitle: lessonData?.title,
      moduleTitle: moduleData?.title,
      lessonId: effectiveLessonId,
      moduleId: effectiveModuleId,
      isLoading,
    }),
    teleprompter: () => ({
      pageTitle: isLoading
        ? "Loading..."
        : `${lessonTitlePrefixes.teleprompter}${lessonData?.title || "Unknown"}`,
      lessonTitle: lessonData?.title,
      moduleTitle: moduleData?.title,
      lessonId: effectiveLessonId,
      moduleId: effectiveModuleId,
      isLoading,
    }),
    lesson: () => ({
      pageTitle: isLoading ? "Loading..." : lessonData?.title || "Unknown",
      lessonTitle: lessonData?.title,
      moduleTitle: moduleData?.title,
      lessonId: lessonData?._id ?? effectiveLessonId,
      moduleId: moduleData?._id ?? effectiveModuleId,
      isLoading,
    }),
    toolbox: () => ({
      pageTitle: isLoading
        ? "Loading..."
        : `${moduleData?.title || "Unknown"}${moduleTitleSuffixes.toolbox}`,
      lessonTitle: undefined,
      moduleTitle: moduleData?.title,
      lessonId: undefined,
      moduleId: effectiveModuleId,
      isLoading,
    }),
    module: () => ({
      pageTitle: isLoading ? "Loading..." : moduleData?.title || "Unknown",
      lessonTitle: undefined,
      moduleTitle: moduleData?.title,
      lessonId: undefined,
      moduleId: effectiveModuleId,
      isLoading,
    }),
    static: () => ({
      pageTitle: getPageTitleFromUrl(pathname) || "Unknown Page",
      lessonTitle: undefined,
      moduleTitle: undefined,
      lessonId: undefined,
      moduleId: undefined,
      isLoading,
    }),
  };

  return contextBuilders[pageType]();
};
